import React, { useState, useEffect, useRef } from "react";
import { LLMMealPlanOutgoing, LLMMealPlanResultOutgoing } from "../../types";
import { SearchResultRowRef } from "../SearchResultRow";

interface NutritionHoverButtonProps {
    title: string;
    dayIndex: number;
    mealType?: keyof LLMMealPlanOutgoing;
    recipeIndex?: number;
    searchResultRowRefs: React.MutableRefObject<Map<string, SearchResultRowRef>>;
    client: any;
    showDetailView: boolean;
    mealPlanData: LLMMealPlanResultOutgoing;
    style?: React.CSSProperties;
}

// Global cache for detailed food data to avoid redundant API calls
const detailedFoodDataCache = new Map<string, any>();

export const NutritionHoverButton: React.FC<NutritionHoverButtonProps> = ({
    title,
    dayIndex,
    mealType,
    recipeIndex,
    searchResultRowRefs,
    client,
    showDetailView,
    mealPlanData,
    style = {},
}) => {
    const [showHover, setShowHover] = useState(false);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
    const [nutrients, setNutrients] = useState<{ name: string; amount: number; unit: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const hoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!showHover || !showDetailView || !client) return;

        const fetchNutrients = async () => {
            setLoading(true);
            const nutrientTotals = new Map<string, { amount: number; unit: string }>();

            try {
                // Determine which ingredients to process based on scope
                const ingredientsToProcess: Array<{ refKey: string }> = [];
                
                if (!mealPlanData) return;

                if (mealType && mealType !== "macros" && recipeIndex !== undefined) {
                    // Single recipe
                    const mealSlot = mealPlanData.mealPlanDays[dayIndex]?.[mealType] as { recipes: any[] };
                    const recipe = mealSlot?.recipes?.[recipeIndex];
                    if (recipe?.ingredients) {
                        recipe.ingredients.forEach((_: any, ingredientIndex: number) => {
                            ingredientsToProcess.push({
                                refKey: `${dayIndex}-${mealType}-${recipeIndex}-${ingredientIndex}`
                            });
                        });
                    }
                } else if (mealType && mealType !== "macros") {
                    // Entire meal
                    const mealSlot = mealPlanData.mealPlanDays[dayIndex]?.[mealType] as { recipes: any[] };
                    if (mealSlot?.recipes) {
                        mealSlot.recipes.forEach((recipe: any, recipeIdx: number) => {
                            if (recipe.ingredients) {
                                recipe.ingredients.forEach((_: any, ingredientIndex: number) => {
                                    ingredientsToProcess.push({
                                        refKey: `${dayIndex}-${mealType}-${recipeIdx}-${ingredientIndex}`
                                    });
                                });
                            }
                        });
                    }
                }

                // Process each ingredient
                for (const { refKey } of ingredientsToProcess) {
                    const rowRef = searchResultRowRefs.current.get(refKey);
                    
                    if (rowRef) {
                        const ingredient = rowRef.getResultWithStateQuantity();
                        
                        if (ingredient.refCode) {
                            let detailedFood = detailedFoodDataCache.get(ingredient.refCode);
                            
                            if (!detailedFood) {
                                try {
                                    const response = await client.getFoodByRefCode(ingredient.refCode);
                                    if (response?.results?.[0]?.ingredients?.[0]) {
                                        detailedFood = response.results[0].ingredients[0];
                                        detailedFoodDataCache.set(ingredient.refCode, detailedFood);
                                    }
                                } catch (error) {
                                    console.error(`Failed to fetch details for ${ingredient.refCode}:`, error);
                                    continue;
                                }
                            }
                            
                            if (detailedFood?.nutrients && ingredient.nutritionPreview?.portion) {
                                const portion = ingredient.nutritionPreview.portion;
                                const effectiveQuantity = portion.suggestedQuantity?.[0] || portion.quantity || 1;
                                const totalWeight = effectiveQuantity * portion.weight.value;
                                const scaleFactor = totalWeight / 100;
                                
                                for (const nutrient of detailedFood.nutrients) {
                                    if (nutrient.nutrient?.name && nutrient.amount !== undefined) {
                                        const name = nutrient.nutrient.name;
                                        const amount = nutrient.amount * scaleFactor;
                                        const unit = nutrient.nutrient.unit || "";
                                        
                                        if (nutrientTotals.has(name)) {
                                            const existing = nutrientTotals.get(name)!;
                                            existing.amount += amount;
                                        } else {
                                            nutrientTotals.set(name, { amount, unit });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                const sortedNutrients = Array.from(nutrientTotals.entries())
                    .map(([name, data]) => ({
                        name,
                        amount: data.amount,
                        unit: data.unit,
                    }))
                    .filter(n => n.amount > 0) // Only show non-zero nutrients
                    .sort((a, b) => {
                        const aStartsWithNumber = /^\d/.test(a.name);
                        const bStartsWithNumber = /^\d/.test(b.name);
                        
                        if (aStartsWithNumber && !bStartsWithNumber) return 1;
                        if (!aStartsWithNumber && bStartsWithNumber) return -1;
                        
                        return a.name.localeCompare(b.name);
                    });

                setNutrients(sortedNutrients);
            } finally {
                setLoading(false);
            }
        };

        fetchNutrients();
    }, [showHover, showDetailView, dayIndex, mealType, recipeIndex, searchResultRowRefs, client, mealPlanData]);

    const handleMouseEnter = () => {
        if (!showDetailView) return;
        
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setHoverPosition({ 
                x: rect.left,
                y: rect.bottom + 5
            });
        }
        
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        
        setShowHover(true);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (relatedTarget && hoverRef.current?.contains(relatedTarget)) {
            return;
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
            setShowHover(false);
        }, 300);
    };

    const handlePanelMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setShowHover(true);
    };

    const handlePanelMouseLeave = () => {
        setShowHover(false);
    };

    if (!showDetailView) return null;

    return (
        <>
            <button
                ref={buttonRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    fontSize: "16px",
                    color: "#6b7280",
                    ...style,
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                    e.currentTarget.style.color = "#3b82f6";
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#6b7280";
                }}
                title="View all nutrients"
            >
                📊
            </button>
            
            {showHover && (
                <div
                    ref={hoverRef}
                    style={{
                        position: "fixed",
                        left: `${hoverPosition.x}px`,
                        top: `${hoverPosition.y}px`,
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        zIndex: 1000,
                        minWidth: "280px",
                        maxWidth: "400px",
                        maxHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    onMouseEnter={handlePanelMouseEnter}
                    onMouseLeave={handlePanelMouseLeave}
                >
                    <div style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                    }}>
                        <div style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                        }}>
                            {title}
                        </div>
                        <div style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "2px",
                        }}>
                            All Nutrients ({nutrients.length} tracked)
                        </div>
                    </div>
                    
                    <div style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "8px 0",
                    }}>
                        {loading ? (
                            <div style={{ 
                                padding: "20px", 
                                textAlign: "center",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                Loading nutrients...
                            </div>
                        ) : nutrients.length === 0 ? (
                            <div style={{ 
                                padding: "20px", 
                                textAlign: "center",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                No detailed nutrient data available
                            </div>
                        ) : (
                            nutrients.map((nutrient, index) => {
                                const formatName = (name: string) => {
                                    return name
                                        .split(/[_\s]+/)
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                        .join(" ");
                                };
                                
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px 20px",
                                            borderBottom: index < nutrients.length - 1 ? "1px solid #f3f4f6" : "none",
                                            transition: "background-color 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#f9fafb";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <span style={{ 
                                            color: "#374151", 
                                            fontSize: "13px",
                                            marginRight: "16px",
                                        }}>
                                            {formatName(nutrient.name)}
                                        </span>
                                        <div style={{ 
                                            display: "flex", 
                                            gap: "4px", 
                                            alignItems: "baseline",
                                            flexShrink: 0,
                                        }}>
                                            <span style={{ 
                                                color: "#1f2937", 
                                                fontSize: "13px",
                                                fontWeight: "600",
                                            }}>
                                                {Math.round(nutrient.amount * 100) / 100}
                                            </span>
                                            <span style={{ 
                                                color: "#6b7280", 
                                                fontSize: "11px",
                                            }}>
                                                {nutrient.unit}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </>
    );
}; 